import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

export interface Me {
  id: string;
  email: string;
  name: string;
  roles: string[];
  isSuperAdmin: boolean;
}

export const useMe = () => useQuery({ queryKey: ['me'], queryFn: () => api.get<Me>('/me') });

/** A row of the tb_Users access table (super-admin Manage Users page). */
export interface AppUser {
  ID: number;
  Name: string;
  EMail: string;
  Role: string | null;
}
export type UserInput = { name: string; email: string; role: string };

export const useUsers = () =>
  useQuery({ queryKey: ['users'], queryFn: () => api.get<AppUser[]>('/users') });

export const useSaveUser = () => {
  const qc = useQueryClient();
  return useMutation({
    // originalEmail set => update (PUT); undefined => create (POST).
    mutationFn: (args: { originalEmail?: string; body: UserInput }) =>
      args.originalEmail
        ? api.put(`/users/${encodeURIComponent(args.originalEmail)}`, args.body)
        : api.post('/users', args.body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
};

export const useDeleteUser = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email: string) => api.del(`/users/${encodeURIComponent(email)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
};

export const useCounts = () =>
  useQuery({
    queryKey: ['counts'],
    queryFn: () => api.get<{ pendingClients: number; pendingEngagements: number }>('/dashboard/counts'),
  });

export interface Metrics {
  totalClients: number;
  totalEngagements: number;
  clientsCreatedViaApp: number;
  engagementsCreatedViaApp: number;
  pendingClients: number;
  pendingEngagements: number;
  completedClients: number;
  completedEngagements: number;
  avgClientCompletionHours: number | null;
  avgClientCompletionDays: number | null;
  avgEngagementCompletionHours: number | null;
  avgEngagementCompletionDays: number | null;
}

export const useMetrics = () =>
  useQuery({
    queryKey: ['metrics'],
    queryFn: () => api.get<Metrics>('/metrics'),
    staleTime: 1000 * 60 * 5,
  });

export interface EtlStatus {
  inProgress: boolean;
  tables: { TableName: string; LatestRunStatus: string }[];
}

/** Maconomy load status. Power Apps polls this every 3s and blocks the data-entry
 *  screens while a load runs; we poll a little less aggressively. */
export const useEtlStatus = () =>
  useQuery({
    queryKey: ['etl-status'],
    queryFn: () => api.get<EtlStatus>('/etl-status'),
    refetchInterval: 15000,
  });

export const useLookup = (name: string, enabled = true) =>
  useQuery({
    queryKey: ['lookup', name],
    queryFn: () => api.get<Record<string, unknown>[]>(`/lookups/${name}`),
    staleTime: 1000 * 60 * 30,
    enabled,
  });

export const useClients = (grouping?: string, search?: string) =>
  useQuery({
    queryKey: ['clients', grouping ?? '', search ?? ''],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (grouping) qs.set('grouping', grouping);
      if (search) qs.set('search', search);
      return api.get<Record<string, unknown>[]>(`/clients?${qs.toString()}`);
    },
  });

/** A row from the client pickers (vw_ClientID_Master / vw_ClientID). */
export interface ClientPickerRow {
  ClientID: string;
  ClientName: string;
  ClientGrouping: string;
  DisplayText?: string;      // master picker: "ClientID - ClientName"
  ClientFullName?: string;   // all-clients picker: "ClientID-ClientName"
  // vw_ClientID also carries these — used to auto-fill the New Engagement client block.
  ClientManager?: string;
  BRCIndustry?: string;
  ClientEmail?: string;
  ContactName?: string;
  FiscalYearEndMonth?: string;
  BillingStatementEmail?: string; // defaults the Email for Monthly Statements field
}

/** Distinct "starts with" letter groupings (alpha-first) for the client pickers. */
export const useClientGroupings = () =>
  useQuery({
    queryKey: ['client-groupings'],
    queryFn: () => api.get<{ ClientGrouping: string }[]>('/clients/lookup/groupings'),
    staleTime: 1000 * 60 * 30,
  });

/**
 * Distinct engagement groupings for the Engagements list filter. Uses vw_Engagement's
 * own ClientGrouping (which buckets C/S names into ranges like 'SN-ST'), matching the
 * ClientGrouping filter — the client-grouping lookup above uses single letters and does
 * NOT match here.
 */
export const useEngagementGroupings = () =>
  useQuery({
    queryKey: ['engagement-groupings'],
    queryFn: () => api.get<{ ClientGrouping: string }[]>('/engagements/lookup/groupings'),
    staleTime: 1000 * 60 * 30,
  });

/**
 * Clients for a grouping. all=false → existing master clients (New Sub picker);
 * all=true → every client master+sub (Bill To = Other picker). Fires only once a
 * grouping is chosen.
 */
export const useClientPicker = (grouping: string | undefined, all: boolean) =>
  useQuery({
    queryKey: ['client-picker', grouping ?? '', all],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (grouping) qs.set('grouping', grouping);
      if (all) qs.set('all', 'true');
      return api.get<ClientPickerRow[]>(`/clients/lookup/ids?${qs.toString()}`);
    },
    enabled: !!grouping,
    staleTime: 1000 * 60 * 5,
  });

export interface TemplateControl {
  ControlKey: string;
  ControlType: 'Deliverable' | 'OtherDetail';
  InputKind: 'checkbox' | 'dropdown' | 'date' | 'text';
  DisplayLabel: string;
  GroupName: string;
  LookupSource: string | null;
  SortOrder: number;
  IsEditable: boolean;
  IsRequired: boolean;
  IsDefaultChecked: boolean;
}

/** DB-driven template rules (deliverables + other-details) for a template. */
export const useTemplateControls = (serviceCategory?: string, templateName?: string) =>
  useQuery({
    queryKey: ['template-controls', serviceCategory ?? '', templateName ?? ''],
    queryFn: () =>
      api.get<TemplateControl[]>(
        `/engagements/templates/controls?serviceCategory=${encodeURIComponent(serviceCategory!)}&templateName=${encodeURIComponent(templateName!)}`,
      ),
    enabled: !!serviceCategory && !!templateName,
    staleTime: 1000 * 60 * 10,
  });

export const useEngagements = (grouping?: string, search?: string, year?: string) =>
  useQuery({
    queryKey: ['engagements', grouping ?? '', search ?? '', year ?? ''],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (grouping) qs.set('grouping', grouping);
      if (search) qs.set('search', search);
      if (year) qs.set('year', year);
      return api.get<Record<string, unknown>[]>(`/engagements?${qs.toString()}`);
    },
  });

export const usePendingClients = () =>
  useQuery({ queryKey: ['pending', 'clients'], queryFn: () => api.get<Record<string, unknown>[]>('/pending/clients') });
export const usePendingEngagements = () =>
  useQuery({ queryKey: ['pending', 'engagements'], queryFn: () => api.get<Record<string, unknown>[]>('/pending/engagements') });

export const useCompletedClients = (search?: string) =>
  useQuery({
    queryKey: ['completed', 'clients', search ?? ''],
    queryFn: () => api.get<Record<string, unknown>[]>(`/completed/clients${search ? `?search=${encodeURIComponent(search)}` : ''}`),
  });
export const useCompletedEngagements = (search?: string) =>
  useQuery({
    queryKey: ['completed', 'engagements', search ?? ''],
    queryFn: () => api.get<Record<string, unknown>[]>(`/completed/engagements${search ? `?search=${encodeURIComponent(search)}` : ''}`),
  });

export const useCreateClient = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: unknown) => api.post('/clients', body),
    onSuccess: () => {
      // Refresh the client grid AND the pickers (New Engagement / New Sub of
      // Existing Client), so a just-created client is searchable right away
      // instead of waiting for the picker cache to go stale.
      qc.invalidateQueries({ queryKey: ['clients'] });
      qc.invalidateQueries({ queryKey: ['client-picker'] });
      qc.invalidateQueries({ queryKey: ['client-groupings'] });
    },
  });
};

export const useCreateEngagement = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: unknown) => api.post('/engagements', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['engagements'] }),
  });
};

export const useUpdateClient = (clientId: string, subId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { body: unknown; clientType: string; opType: string }) =>
      api.put(`/clients/${clientId}/${subId}?clientType=${encodeURIComponent(args.clientType)}&opType=${args.opType}`, args.body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  });
};

export const useUpdateEngagement = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { body: unknown; opType: string }) =>
      api.put(`/engagements/${id}?opType=${args.opType}`, args.body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['engagements'] }),
  });
};

export const useEngagement = (id?: string) =>
  useQuery({ queryKey: ['engagement', id], queryFn: () => api.get<Record<string, unknown>>(`/engagements/${id}`), enabled: !!id });

export const useClient = (clientId?: string, subId?: string) =>
  useQuery({
    queryKey: ['client', clientId, subId],
    queryFn: () => api.get<Record<string, unknown>>(`/clients/${clientId}/${subId}`),
    enabled: !!clientId && !!subId,
  });

// ---- Request forms (Termination / Reactivation / ...) ----
export interface FormClientRow {
  ClientID: string;
  ClientName: string;
  ClientFullName: string;
  Terminated?: number;
}

/** Server-side client search for the request-form pickers ("starts with" + name). */
export const useFormClients = (scope: 'active' | 'all', q: string, grouping = '') =>
  useQuery({
    queryKey: ['form-clients', scope, q, grouping],
    queryFn: () =>
      api.get<FormClientRow[]>(
        `/forms/clients?scope=${scope}&q=${encodeURIComponent(q)}&grouping=${encodeURIComponent(grouping)}`,
      ),
    enabled: q.trim().length >= 2 || grouping.trim().length > 0,
    staleTime: 30_000,
  });

export interface ClientFormBody {
  clients: { clientId: string; clientName: string }[];
  effectiveDate: string;
  notes?: string;
}

const useSubmitClientForm = (path: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ClientFormBody) => api.post<{ submissionId: number }>(path, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['form-submissions'] }),
  });
};
export const useSubmitTermination = () => useSubmitClientForm('/forms/termination');
export const useSubmitReactivation = () => useSubmitClientForm('/forms/reactivation');

export interface EngagementRow {
  EngagementID: string;
  ServiceCategory: string;
  TemplateName: string;
  Year: number;
}
/** A client's engagements, for the Discontinuation form. */
export const useClientEngagements = (clientId?: string) =>
  useQuery({
    queryKey: ['client-engagements', clientId ?? ''],
    queryFn: () => api.get<EngagementRow[]>(`/forms/engagements?clientId=${encodeURIComponent(clientId ?? '')}`),
    enabled: !!clientId,
  });

export interface DiscontinuationBody {
  clientId: string;
  clientName: string;
  engagements: { engagementId: string; serviceCategory?: string; templateName?: string }[];
  effectiveDate: string;
  notes?: string;
}
export const useSubmitDiscontinuation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: DiscontinuationBody) => api.post<{ submissionId: number }>('/forms/discontinuation', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['form-submissions'] }),
  });
};

export interface SubmissionFilters {
  formType?: string;
  q?: string;
  from?: string;
  to?: string;
}
/** Distinct form types that have submissions, for the filter dropdown. */
export const useSubmissionTypes = () =>
  useQuery({ queryKey: ['form-submission-types'], queryFn: () => api.get<string[]>('/forms/submission-types') });

export const useFormSubmissions = (filters: SubmissionFilters = {}) =>
  useQuery({
    queryKey: ['form-submissions', filters.formType ?? '', filters.q ?? '', filters.from ?? '', filters.to ?? ''],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (filters.formType) qs.set('formType', filters.formType);
      if (filters.q) qs.set('q', filters.q);
      if (filters.from) qs.set('from', filters.from);
      if (filters.to) qs.set('to', filters.to);
      return api.get<Record<string, unknown>[]>(`/forms/submissions${qs.toString() ? `?${qs.toString()}` : ''}`);
    },
  });
